import type Point from "@arcgis/core/geometry/Point";
import type Graphic from "@arcgis/core/Graphic";
import { AppConfig } from "../AppConfig";
import { deepCopy, uuidv4 } from "./helpers";
import { LighthouseData, Sector } from "./lighthouseConstants";
import { NFSStatus } from "./nfsUtils";

/**
 * Fyrstasjon and Fyrlykt are configured in different groups, so the entry holding the
 * attributes a new lighthouse starts out with has to be looked up per type.
 */
const getInitialAttributes = (isFyrstasjon: boolean) =>
    isFyrstasjon
        ? AppConfig.CreateObject.find((group) => group.GroupName === "Lys")?.GroupEntries.find(
              (entry) => entry.Title === "Fyrstasjon"
          )?.FieldValues
        : AppConfig.CreateObject.find((group) => group.GroupName === "Fyrlykt")?.GroupEntries.find(
              (entry) => entry.Title === "Fyrlykt"
          )?.FieldValues;

export interface BuildLighthouseDataOptions {
    /** Where the lighthouse is placed - the user picks this on the map. */
    center: Point;
    isFyrstasjon: boolean;
    /** Sectors to start from. Defaults to the configured default sectors. */
    sectors?: Sector[];
    setState?: LighthouseData["setState"];
}

/**
 * Builds the LighthouseData for a new lighthouse and seeds the feature with the attributes
 * its type starts out with. Pure apart from mutating the feature it is handed - saving and
 * anything else touching the map lives in createLighthouseAtPoint.
 */
export const buildLighthouseData = (newFeature: Graphic, options: BuildLighthouseDataOptions): LighthouseData => {
    const { center, isFyrstasjon, sectors, setState } = options;

    const lighthouseId = uuidv4();

    const lighthouseData: LighthouseData = {
        id: lighthouseId,
        center,
        // Copied so the configured defaults are not stamped with this lighthouse's ids
        sectors: deepCopy(sectors ?? AppConfig.Lighthouse.DefaultSectors),
        setState,
    };

    for (const sector of lighthouseData.sectors) {
        sector.lighthouseId = lighthouseId;
        sector.globalid = uuidv4();
    }

    for (const attribute of getInitialAttributes(isFyrstasjon) ?? []) {
        newFeature.attributes[attribute.FieldName] = attribute.Value;
    }

    newFeature.attributes.lighthouseData = lighthouseData;

    return lighthouseData;
};

/**
 * Normalises sectors for adoption into a brand-new lighthouse: the previous owner's
 * identity and statuses are dropped, geometry and description are kept. Used by both the
 * table conversion and the replace flow, so a copied sector never publishes itself as
 * pre-existing or collides with rows the replaced object still owns.
 */
export const adoptSectorsAsNew = (sectors: Sector[]): Sector[] =>
    deepCopy(sectors).map((sector: Sector, index: number) => ({
        ...sector,
        globalid: "",
        lighthouseId: "",
        index,
        nfs_status: NFSStatus.PROJECTED,
        nfs_status_line1: NFSStatus.PROJECTED,
        nfs_status_line2: NFSStatus.PROJECTED,
    }));
